import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuthContext } from '@/contexts/AuthContext';

export type SpecialtyDomain = 'orthopedics' | 'podiatry';

interface UseActiveSpecialtiesResult {
  loading: boolean;
  specialties: SpecialtyDomain[];
  hasOrthopedics: boolean;
  hasPodiatry: boolean;
  /** True when the tenant has multiple specialties — show the switcher. */
  showSwitcher: boolean;
}

const DEFAULT: SpecialtyDomain[] = ['orthopedics'];

/**
 * Reads `clients.active_specialties` for the current user's primary client and
 * returns booleans for the supported specialty domains. Falls back to
 * `['orthopedics']` for users without a client assignment (or while loading)
 * so existing flows keep working.
 */
export function useActiveSpecialties(): UseActiveSpecialtiesResult {
  const { user } = useAuthContext();
  const [loading, setLoading] = useState(true);
  const [specialties, setSpecialties] = useState<SpecialtyDomain[]>(DEFAULT);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      if (!user?.id) {
        if (!cancelled) {
          setSpecialties(DEFAULT);
          setLoading(false);
        }
        return;
      }

      try {
        const { data: assignment } = await supabase
          .from('user_client_assignments')
          .select('client_id')
          .eq('user_id', user.id)
          .eq('is_primary', true)
          .maybeSingle();

        if (!assignment?.client_id) {
          if (!cancelled) {
            setSpecialties(DEFAULT);
            setLoading(false);
          }
          return;
        }

        const { data: client } = await supabase
          .from('clients')
          .select('active_specialties')
          .eq('id', assignment.client_id)
          .maybeSingle();

        const raw = (client?.active_specialties as string[] | null) ?? DEFAULT;
        const filtered = raw.filter(
          (s): s is SpecialtyDomain => s === 'orthopedics' || s === 'podiatry'
        );

        if (!cancelled) {
          setSpecialties(filtered.length ? filtered : DEFAULT);
          setLoading(false);
        }
      } catch (err) {
        console.error('[useActiveSpecialties] failed to load:', err);
        if (!cancelled) {
          setSpecialties(DEFAULT);
          setLoading(false);
        }
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [user?.id]);

  return {
    loading,
    specialties,
    hasOrthopedics: specialties.includes('orthopedics'),
    hasPodiatry: specialties.includes('podiatry'),
    showSwitcher: specialties.length > 1,
  };
}
